// eslint-disable-next-line @altermaind/you-shall-not-pass
import { useEffect } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Form } from '@/_shared/components/Form';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { FileTypes } from '@/_shared/types';
import { CallResponseGetAspect, getAspectCall, getAspectColorsCall } from '@/aspect/state/api';
import { useAspect } from '@/aspect/state/hooks';
import { LoaderFullPage } from '@/configuration/components/LoaderFullPage';
import { ColorsList } from '../ColorsList';
import { PreviewPlatform } from '../PreviewPlatform';

export const AspectTab = () => {
  const t = useTranslations('settings.tabs.aspect');
  const tShared = useTranslations();
  const { genericResponsiveValue } = useResponsiveStyle();

  const {
    buttonGenerateLogoRef,
    inputSiteRef,
    logoLight,
    setLogoLight,
    logoDark,
    setLogoDark,
    gapXS,
    gapXXXS,
    colorSuggested,
    onSelectedNewColor,
    generateLogo,
    control,
    isValid,
    loadingPostAspectGenerate,
    errorPostAspectGenerate,
    errorPostAspectGenerateColors,
    errorPostAspectGenerateLogo,
    dark,
    setDark,
    onSubmit,
    loadingPutAspectSave,
    dynamicHeight,
    trigger,
  } = useAspect();

  const {
    execute: executeGetAspect,
    loading: loadingGetAspect,
    error: errorGetAspect,
    success: successGetAspect,
    totalDesignQuota,
    remainingDesignQuota,
  } = getAspectCall.hook();
  const {
    execute: executeGetAspectColors,
    response: responseGetAspectColors,
    loading: loadingGetAspectColors,
    error: errorGetAspectColors,
    success: successGetAspectColors,
  } = getAspectColorsCall.hook();

  useEffect(() => {
    executeGetAspectColors({
      successSaga: () => {
        executeGetAspect({
          successSaga: ({ response }) => {
            const responseGetAspect = response as CallResponseGetAspect;
            if (responseGetAspect?.logo?.light) {
              setLogoLight(responseGetAspect.logo.light);
            }
            if (responseGetAspect?.logo?.dark) {
              setLogoDark(responseGetAspect.logo.dark);
            }
            if (responseGetAspect?.color?.id) {
              onSelectedNewColor(responseGetAspect?.color?.id);
            }
          },
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingGetAspect || loadingGetAspectColors) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetAspect || errorGetAspectColors) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge iconName='info' type='error' label={message} />;
  }

  if (
    successGetAspectColors &&
    successGetAspect &&
    !loadingGetAspect &&
    !errorGetAspect &&
    !loadingGetAspectColors &&
    !errorGetAspectColors &&
    responseGetAspectColors
  ) {
    return (
      <>
        <Card mt='xs'>
          <Box>
            <Typography variant='h1' mb='xs'>
              {t('title')}
            </Typography>
          </Box>
          <Box
            mt='s'
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
          >
            <Box
              flex='1'
              mr={genericResponsiveValue({
                breakPoints: { desktop: 's' },
                defaultValue: 'none',
              })}
            >
              <Typography variant='p2/light' mb='s'>
                {t('description')}
              </Typography>
              <Box position='relative'>
                <Form.FormInput
                  control={control}
                  additonalProps={{
                    required: true,
                    pattern: {
                      /* eslint-disable no-useless-escape */
                      value: /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/,
                    },
                  }}
                  avoidRequiredStarOnLabel
                  name='site'
                  label={t('siteLabel')}
                  placeholder={t('sitePlaceholder')}
                  autoComplete='off'
                  loading={loadingPostAspectGenerate}
                  flex={1}
                  inputTextStyle={{
                    pr: buttonGenerateLogoRef.current
                      ? buttonGenerateLogoRef.current.clientWidth + (gapXS || 0)
                      : undefined,
                  }}
                  disabled={remainingDesignQuota === 0}
                  inputRef={inputSiteRef}
                />
                <Button
                  forwardedRef={buttonGenerateLogoRef}
                  variant='primary'
                  minWidth='none'
                  onClick={() => generateLogo(remainingDesignQuota)}
                  disabled={!isValid || remainingDesignQuota === 0 || loadingPostAspectGenerate}
                  px='xs'
                  position='absolute'
                  right={gapXXXS}
                  bottom={gapXXXS}
                  height={dynamicHeight}
                >
                  {t('cta.generate')}
                </Button>
              </Box>
              <Typography variant='p2/light' mt='xxs' color='grey/light'>
                {t('siteDescription', {
                  remainingDesignQuota,
                  totalDesignQuota,
                })}
              </Typography>
              {remainingDesignQuota === 0 && (
                <Box gridColumn='1/span 2' flex={1} mt='s' mb='xxs'>
                  <AlertBadge
                    iconName='info'
                    type='error'
                    label={t('remainingDesignQuotaZeroAlert')}
                  />
                </Box>
              )}
              {!errorPostAspectGenerate && errorPostAspectGenerateLogo && (
                <Box gridColumn='1/span 2' flex={1} mt='s' mb='xxs'>
                  <AlertBadge iconName='info' type='error' label={t('alertError')} />
                </Box>
              )}
              {!errorPostAspectGenerate && errorPostAspectGenerateColors && (
                <Box gridColumn='1/span 2' flex={1} mt='s' mb='xxs'>
                  <AlertBadge iconName='info' type='error' label={t('alertErrorColors')} />
                </Box>
              )}
              <Box gridColumn='1/span 2' flex={1} mt='s' mb='m'>
                <AlertBadge iconName='lightBulb' type='transparent' label={t('alert')} />
              </Box>
              <Box
                display='flex'
                flexDirection={genericResponsiveValue({
                  breakPoints: { desktop: 'row' },
                  defaultValue: 'column',
                })}
                style={{ gap: gapXS }}
              >
                <Box flex='1'>
                  <Form.FormFileInput
                    label={t('uploadLogo.titleWhiteMode')}
                    control={control}
                    trigger={trigger}
                    tooltip={t('uploadLogo.titleTooltipWhiteMode')}
                    name='uploadLogoLight'
                    supportedFileTypes={[
                      FileTypes.PNG_IMAGES,
                      FileTypes.JPEG_IMAGES,
                      FileTypes.SVG,
                    ]}
                    value={logoLight}
                  />
                </Box>
                <Box
                  flex='1'
                  mt={genericResponsiveValue({
                    breakPoints: { desktop: 'none' },
                    defaultValue: 's',
                  })}
                >
                  <Form.FormFileInput
                    label={t('uploadLogo.titleDarkMode')}
                    control={control}
                    trigger={trigger}
                    tooltip={t('uploadLogo.titleTooltipDarkMode')}
                    name='uploadLogoDark'
                    supportedFileTypes={[
                      FileTypes.PNG_IMAGES,
                      FileTypes.JPEG_IMAGES,
                      FileTypes.SVG,
                    ]}
                    value={logoDark}
                  />
                </Box>
              </Box>
              <Box mt='m'>
                <ColorsList
                  responseGetAspectColors={responseGetAspectColors}
                  onSelectedNewColor={onSelectedNewColor}
                  colorSuggested={colorSuggested}
                />
              </Box>
            </Box>
            <Box
              flex='1'
              ml={genericResponsiveValue({
                breakPoints: { desktop: 's' },
                defaultValue: 'none',
              })}
            >
              <PreviewPlatform dark={dark} setDark={setDark} colorSuggested={colorSuggested} />
            </Box>
          </Box>
          <Box
            mt={genericResponsiveValue({
              breakPoints: { desktop: 'none' },
              defaultValue: 'xl',
            })}
            display='flex'
            justifyContent='flex-end'
          >
            <Button
              disabled={loadingPutAspectSave || (!logoLight && !logoDark && !colorSuggested)}
              loading={loadingPutAspectSave}
              variant='primary'
              mt='xxs'
              onClick={onSubmit}
            >
              {t('cta.save')}
            </Button>
          </Box>
        </Card>
        {loadingPostAspectGenerate && (
          <LoaderFullPage title={t('loading.title')} description={t('loading.description')} />
        )}
      </>
    );
  }

  return null;
};
